import "./CSVApproval.scss";

import React, { useEffect, useRef, useState } from "react";

import { CleanTable } from "../../../components/table/CleanTable";
import SelectWithSearchBox from "../../../pages/townson-dashboard-list/components/client-data-upload-component/select-with-search-box";
const SHORT_ZIP_CODE_PATTERN = /(^|[\s,])(\d{4})(-\d{4})?(\s*)$/;

function formatLocationZipCode(location) {
	return String(location).replace(SHORT_ZIP_CODE_PATTERN, (_, prefix, zipCode, zipPlusFour = "", trailingSpace) => {
		return `${prefix}0${zipCode}${zipPlusFour}${trailingSpace}`;
	});
}

function CSVApprovalScreen({ csvApproval, setCsvApproval, setTempResults }) {
	const [editingIndex, setEditingIndex] = useState(null);
	const [selectedItems, setSelectedItems] = useState([]);
	const editingInputRef = useRef(null);

	const scrollEditingInputIntoView = (block = "center") => {
		editingInputRef.current?.scrollIntoView({ behavior: "smooth", block });
	};

	const scrollResultsIntoView = () => {
		window.setTimeout(() => {
			const input = editingInputRef.current;
			const results = input?.parentElement?.querySelector(".select-items-wrapper.show");
			if (!input || !results) return;

			let scrollParent = input.parentElement;
			while (scrollParent && scrollParent !== document.body) {
				const styles = window.getComputedStyle(scrollParent);
				if (/(auto|scroll)/.test(styles.overflowY)) break;
				scrollParent = scrollParent.parentElement;
			}

			const container = scrollParent || document.scrollingElement;
			const resultRect = results.getBoundingClientRect();
			const containerRect =
				container === document.scrollingElement
					? { top: 0, bottom: window.innerHeight }
					: container.getBoundingClientRect();
			const bottomPadding = 24;

			if (resultRect.bottom > containerRect.bottom - bottomPadding) {
				container.scrollBy({
					top: resultRect.bottom - containerRect.bottom + bottomPadding,
					behavior: "smooth",
				});
			}
		}, 100);
	};

	useEffect(() => {
		if (editingIndex === null) return;

		const focusTimeout = window.setTimeout(() => {
			editingInputRef.current?.focus();
			scrollEditingInputIntoView("center");
		}, 0);

		return () => window.clearTimeout(focusTimeout);
	}, [editingIndex]);
	const updateRow = (index, changes) => {
		const updateRows = (rows) => rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...changes } : row));
		setCsvApproval(updateRows);
		setTempResults(updateRows);
	};

	const handleAddressChange = (index, selectedLocation) => {
		if (!selectedLocation) {
			updateRow(index, {
				manualAddress: "",
				geocodedAddress: "",
				latitude: null,
				longitude: null,
				approved: false,
				geocodeStatus: "manual",
			});
			return;
		}

		updateRow(index, {
			manualAddress: "",
			geocodedAddress: selectedLocation.address?.freeformAddress ?? "",
			latitude: selectedLocation.position?.lat ?? null,
			longitude: selectedLocation.position?.lon ?? null,
			approved: false,
			geocodeStatus: selectedLocation.position ? "Geocoded" : "No result found",
			geocodeError: selectedLocation.position ? "" : "Selected address has no coordinates.",
		});
	};

	const approveRow = (index) => {
		updateRow(index, { approved: true });
		setEditingIndex(null);
	};

	const removeLocation = (index) => {
		const removeRow = (rows) => rows.filter((_, rowIndex) => rowIndex !== index);
		setCsvApproval(removeRow);
		setTempResults(removeRow);
	};

	const approveResults = () => {
		const selectedIds = new Set(selectedItems.map((item) => item.id));
		const approveRows = (rows) =>
			rows.map((row, index) => {
				const rowId = row.rowIndex ?? index;
				const shouldApprove = selectedItems.length === 0 || selectedIds.has(rowId);
				return shouldApprove && row.latitude != null && row.longitude != null
					? { ...row, approved: true }
					: row;
			});
		setCsvApproval(approveRows);
		setTempResults(approveRows);
		setEditingIndex(null);
	};

	const tableData = csvApproval.map((row, index) => ({ ...row, id: row.rowIndex ?? index, reviewIndex: index }));
	const hasCoordinates = (row) => row.latitude != null && row.longitude != null;
	const selectedRows = tableData.filter((row) => selectedItems.some((item) => item.id === row.id));
	const canApprove = selectedItems.length
		? selectedRows.length > 0 && selectedRows.every(hasCoordinates)
		: tableData.length > 0 && tableData.every(hasCoordinates);

	const tableColumns = [
		{
			header: "Imported record",
			render: (row) => {
				console.log("[CSV approval debug] imported cell", {
					rowIndex: row.reviewIndex,
					location: row.Location,
					sourcePropertyAddress: row.sourcePropertyAddress,
				});

				return (
					<div className='csvApprovalImported'>
						<span>{formatLocationZipCode(row.Location)}</span>
					</div>
				);
			},
		},
		{
			header: "Suggested geocode result",
			render: (row) => {
				const selectedAddress = row.manualAddress || row.geocodedAddress || "";
				const address = selectedAddress || "No result found";
				return (
					<div className='csvApprovalMapped'>
						{editingIndex === row.reviewIndex ? (
							<SelectWithSearchBox
								selectedProposedLocation={[
									{
										address: { freeformAddress: selectedAddress },
										position: { lat: row.latitude, lon: row.longitude },
									},
								]}
								setSelectedItem={(value) => handleAddressChange(row.reviewIndex, value)}
								inputRef={editingInputRef}
								onResultsReady={scrollResultsIntoView}
								index={0}
								lableName='Edit suggested address'
							/>
						) : (
							<span>{address}</span>
						)}
					</div>
				);
			},
		},
		{
			header: "Result",
			render: (row) => {
				const status = row.geocodeStatus || "No match";
				const isExactMatch = status === "Geocoded";
				console.log("[CSV approval debug] rendered row", {
					rowIndex: row.reviewIndex,
					importedLocation: row.Location,
					suggestedAddress: row.geocodedAddress,
					geocodeStatus: row.geocodeStatus,
					derivedStatus: status,
					latitude: row.latitude,
					longitude: row.longitude,
				});
				return (
					<span className={isExactMatch ? "text-success" : status === "Partial match" ? "text-warning" : "text-danger"}>
						{isExactMatch ? "Match found" : status === "Partial match" ? "Partial match: " : ""}
						{isExactMatch ? "" : row.geocodeError || "No matching address found."}
					</span>
				);
			},
		},
	];

	return (
		<div className='csvRows container'>
			<div className='d-flex justify-content-end' style={{ marginBottom: "24px" }}>
				<button type='button' className='btn primary' disabled={!canApprove} onClick={approveResults}>
					{selectedItems.length ? "Approve Selected Results" : "Approve All Results"}
				</button>
			</div>
			<div className='approval-table-wrapper'>
				<CleanTable
					className='csvApprovalTable'
					tableData={tableData}
					tableColumns={tableColumns}
					allowCheckboxes
					selectedItems={selectedItems}
					setSelectedItems={setSelectedItems}
					hasEdit
					hasDelete
					groupActions={(row) => (
						<div className='csvApprovalActions'>
							<button
								type='button'
								className={row.approved ? "btn btn-sm success" : "btn success outline btn-sm "}
								disabled={!hasCoordinates(row)}
								onClick={() => approveRow(row.reviewIndex)}
							>
								{row.approved ? "Approved" : "Approve"}
							</button>
							<button
								type='button'
								className='btn secondary outline btn-sm'
								onClick={() =>
									setEditingIndex(editingIndex === row.reviewIndex ? null : row.reviewIndex)
								}
							>
								{editingIndex === row.reviewIndex ? "Done" : "Edit"}
							</button>
							<button
								type='button'
								className='btn danger btn-sm'
								onClick={() => removeLocation(row.reviewIndex)}
							>
								Remove
							</button>
						</div>
					)}
				/>
			</div>
		</div>
	);
}

export { CSVApprovalScreen };
